import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/api.service';
import Swal from 'sweetalert2';

const Dashboard = () => {
  const [searchParams] = useSearchParams();
  const returnUri = searchParams.get('return_uri');
  const { user, logout, refreshUserData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    ageRange: '',
    gender: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [domainConfig, setDomainConfig] = useState(null);
  const injectedScriptRefs = useRef([]);

  useEffect(() => {
    // Simulate loading time for better UX
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    fetch(`/api/domain-config?host=${encodeURIComponent(host)}`)
      .then(res => (res.ok ? res.json().catch(() => null) : null))
      .then(data => {
        if (!data || typeof data !== 'object') return;
        try {
          setDomainConfig(data);
          const root = document.documentElement?.style;
          if (root && data.colors && typeof data.colors === 'object') {
            if (data.colors.primary) root.setProperty('--primary-color', data.colors.primary);
            if (data.colors.primaryHover) root.setProperty('--primary-hover', data.colors.primaryHover);
            if (data.colors.background) root.setProperty('--background', data.colors.background);
            if (data.colors.surface) root.setProperty('--surface', data.colors.surface);
            if (data.colors.textPrimary) root.setProperty('--text-primary', data.colors.textPrimary);
            if (data.colors.textSecondary) root.setProperty('--text-secondary', data.colors.textSecondary);
          }
          const snippets = Array.isArray(data.snippets) ? data.snippets : [];
          snippets.forEach(s => {
            if (!s || s.tag !== 'script' || !s.src) return;
            try {
              const el = document.createElement('script');
              el.src = s.src;
              if (s.async) el.async = true;
              if (s.defer) el.defer = true;
              if (typeof s.type === 'string' && s.type) el.type = s.type;
              if (s['data-id']) el.setAttribute('data-id', s['data-id']);
              document.head.appendChild(el);
              injectedScriptRefs.current.push(el);
            } catch (_) {}
          });
        } catch (_) {}
      })
      .catch(() => {});
    return () => {
      injectedScriptRefs.current.forEach(el => {
        try {
          el.remove();
        } catch (_) {}
      });
      injectedScriptRefs.current = [];
    };
  }, []);

  const handleEditClick = () => {
    setEditForm({
      name: user?.userData?.name || '',
      ageRange: user?.userData?.ageRange || '',
      gender: user?.userData?.gender || ''
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!editForm.name.trim()) {
      errors.name = 'El nombre es requerido';
    } else if (editForm.name.trim().length < 2) {
      errors.name = 'El nombre debe tener al menos 2 caracteres';
    }
    
    if (!editForm.ageRange) {
      errors.ageRange = 'Por favor selecciona tu rango de edad';
    }
    
    if (!editForm.gender) {
      errors.gender = 'Por favor selecciona tu género';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare the data to update
      const updateData = {
        name: editForm.name.trim(),
        ageRange: editForm.ageRange,
        gender: editForm.gender
      };

      // Call the API to update user profile
      await userService.updateUser(user.uid, updateData);
      
      await Swal.fire({
        icon: 'success',
        title: '¡Perfil actualizado!',
        text: 'Tus datos han sido guardados exitosamente.',
        timer: 2000,
        showConfirmButton: false,
        timerProgressBar: true,
        customClass: {
          popup: 'swal2-custom-popup'
        }
      });
      
      setShowEditModal(false);
      // Refresh user data to show updated information
      await refreshUserData();
    } catch (error) {
      console.error('Error updating profile:', error);
      
      let errorMessage = 'No se pudo actualizar el perfil. Intenta de nuevo.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.error) {
        errorMessage = error.error;
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Error al actualizar',
        text: errorMessage,
        confirmButtonColor: '#667eea'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: '¿Cerrar sesión?',
      text: '¿Estás seguro de que quieres cerrar sesión?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#667eea',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, cerrar sesión',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await logout();
      } catch (error) {
        console.error('Error during logout:', error);
      }
    }
  };

  const handleRefreshProfile = async () => {
    try {
      await refreshUserData();
      Swal.fire({
        icon: 'success',
        title: 'Perfil actualizado',
        text: 'Tu perfil ha sido actualizado exitosamente.',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar el perfil. Intenta de nuevo.',
      });
    }
  };

  const handleDeleteAccount = async () => {
    const result = await Swal.fire({
      title: '¿Estás seguro de que quieres borrar tu cuenta?',
      text: 'Esta acción es irreversible. Se eliminará tu cuenta de Firebase y todos tus datos. No podrás recuperar tu cuenta.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, borrar cuenta',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        // Show loading state
        Swal.fire({
          title: 'Borrando cuenta...',
          text: 'Por favor espera mientras se elimina tu cuenta',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Delete user from backend (this will also delete Firebase account)
        await userService.deleteUser(user.uid);
        
        // Close loading dialog
        Swal.close();
        
        // Show success message
        await Swal.fire({
          icon: 'success',
          title: 'Cuenta borrada',
          text: 'Tu cuenta ha sido eliminada exitosamente.',
          timer: 2000,
          showConfirmButton: false,
          timerProgressBar: true,
          customClass: {
            popup: 'swal2-custom-popup'
          }
        });

        // Logout and redirect to login
        await logout();
        
      } catch (error) {
        console.error('Error deleting account:', error);
        
        // Close loading dialog
        Swal.close();
        
        let errorMessage = 'No se pudo borrar la cuenta. Intenta de nuevo.';
        if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        }
        
        Swal.fire({
          icon: 'error',
          title: 'Error al borrar cuenta',
          text: errorMessage,
          confirmButtonColor: '#e74c3c'
        });
      }
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingCard>
          <LoadingSpinner />
          <p>Cargando perfil...</p>
        </LoadingCard>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Title>Dashboard - Liga Estrella</Title>
          <HeaderButtons>
            {returnUri && (
              <BackToClientButton onClick={() => window.location.href = returnUri}>
                Volver
              </BackToClientButton>
            )}
            <LogoutButton onClick={handleLogout}>
              Cerrar Sesión
            </LogoutButton>
          </HeaderButtons>
        </HeaderContent>
      </Header>

      <Content>
        <ProfileCard>
          <CardHeader>
            <CardTitle>👤 Tu Perfil</CardTitle>
            <RefreshButton onClick={handleRefreshProfile}>
              🔄
            </RefreshButton>
          </CardHeader>
          <UserInfo>
            <InfoRow>
              <InfoLabel>Nombre:</InfoLabel>
              <InfoValue>{user?.userData?.name || 'No disponible'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Email:</InfoLabel>
              <InfoValue>{user?.userData?.email || user?.email || 'No disponible'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Teléfono:</InfoLabel>
              <InfoValue>{user?.userData?.phone || user?.phoneNumber || 'No disponible'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Edad:</InfoLabel>
              <InfoValue>{user?.userData?.ageRange || 'No especificado'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Género:</InfoLabel>
              <InfoValue>
                {user?.userData?.gender === 'male' ? 'Hombre' : 
                 user?.userData?.gender === 'female' ? 'Mujer' : 
                 user?.userData?.gender === 'not_specified' ? 'No especificar' : 
                 'No disponible'}
              </InfoValue>
            </InfoRow>
          </UserInfo>
          
          <ButtonGroup>
            <EditButton onClick={handleEditClick}>
              ✏️ Editar Perfil
            </EditButton>
            <DeleteButton onClick={handleDeleteAccount}>
              🗑️ Borrar Cuenta
            </DeleteButton>
          </ButtonGroup>
        </ProfileCard>

        <Footer>
          <FooterText>
            Liga Estrella © 2024
          </FooterText>
          <LegalLinks>
            {domainConfig?.legalUrl ? (
              <LegalLink href={domainConfig.legalUrl} target="_blank" rel="noopener noreferrer">
                Aviso legal
              </LegalLink>
            ) : (
              <LegalLink href="#" onClick={(e) => e.preventDefault()} aria-hidden>
                Aviso legal
              </LegalLink>
            )}
            {domainConfig?.privacyUrl ? (
              <LegalLink href={domainConfig.privacyUrl} target="_blank" rel="noopener noreferrer">
                Política de privacidad y cookies
              </LegalLink>
            ) : (
              <LegalLink href="#" onClick={(e) => e.preventDefault()} aria-hidden>
                Política de privacidad y cookies
              </LegalLink>
            )}
            {domainConfig?.termsUrl ? (
              <LegalLink href={domainConfig.termsUrl} target="_blank" rel="noopener noreferrer">
                Términos y condiciones
              </LegalLink>
            ) : (
              <LegalLink href="#" onClick={(e) => e.preventDefault()} aria-hidden>
                Términos y condiciones
              </LegalLink>
            )}
          </LegalLinks>
        </Footer>
      </Content>

      {/* Enhanced Edit Modal */}
      {showEditModal && (
        <ModalOverlay onClick={() => setShowEditModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <EditIcon>✏️</EditIcon>
                Editar Perfil
              </ModalTitle>
              <CloseButton onClick={() => setShowEditModal(false)}>×</CloseButton>
            </ModalHeader>
            
            <Form onSubmit={handleEditSubmit}>
              <FormSection>
                <SectionTitle>Información Personal</SectionTitle>
                
                <FormGroup>
                  <FormLabel>
                    Nombre completo <RequiredStar>*</RequiredStar>
                  </FormLabel>
                  <InputWrapper>
                    <FormInput
                      type="text"
                      value={editForm.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Tu nombre completo"
                      $hasError={!!formErrors.name}
                    />
                    {formErrors.name && <ErrorMessage>{formErrors.name}</ErrorMessage>}
                  </InputWrapper>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Email</FormLabel>
                  <InputWrapper>
                    <FormInput
                      type="email"
                      value={user?.userData?.email || user?.email || ''}
                      disabled
                      $disabled={true}
                    />
                    <FormHelp>El email no se puede modificar</FormHelp>
                  </InputWrapper>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Teléfono</FormLabel>
                  <InputWrapper>
                    <FormInput
                      type="tel"
                      value={user?.userData?.phone || user?.phoneNumber || ''}
                      disabled
                      $disabled={true}
                    />
                    <FormHelp>El teléfono no se puede modificar</FormHelp>
                  </InputWrapper>
                </FormGroup>

                <FormGroup>
                  <FormLabel>
                    Rango de edad <RequiredStar>*</RequiredStar>
                  </FormLabel>
                  <InputWrapper>
                    <FormSelect
                      value={editForm.ageRange}
                      onChange={(e) => handleInputChange('ageRange', e.target.value)}
                      $hasError={!!formErrors.ageRange}
                    >
                      <option value="">Selecciona tu edad</option>
                      <option value="18-24">18-24 años</option>
                      <option value="25-34">25-34 años</option>
                      <option value="35-44">35-44 años</option>
                      <option value="45-54">45-54 años</option>
                      <option value="55-64">55-64 años</option>
                      <option value="65+">65+ años</option>
                    </FormSelect>
                    {formErrors.ageRange && <ErrorMessage>{formErrors.ageRange}</ErrorMessage>}
                  </InputWrapper>
                </FormGroup>

                <FormGroup>
                  <FormLabel>
                    Género <RequiredStar>*</RequiredStar>
                  </FormLabel>
                  <InputWrapper>
                    <FormSelect
                      value={editForm.gender}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                      $hasError={!!formErrors.gender}
                    >
                      <option value="">Selecciona tu género</option>
                      <option value="male">Hombre</option>
                      <option value="female">Mujer</option>
                      <option value="not_specified">No especificar</option>
                    </FormSelect>
                    {formErrors.gender && <ErrorMessage>{formErrors.gender}</ErrorMessage>}
                  </InputWrapper>
                </FormGroup>
              </FormSection>

              <ButtonGroup>
                <CancelButton 
                  type="button" 
                  onClick={() => setShowEditModal(false)}
                  disabled={isSubmitting}
                >
                  Cancelar
                </CancelButton>
                <SaveButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Spinner />
                      Guardando...
                    </>
                  ) : (
                    'Guardar Cambios'
                  )}
                </SaveButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: var(--background);
`;

const Header = styled.header`
  background: var(--surface);
  border-bottom: 1px solid var(--border-light);
  padding: 1rem 0;
  box-shadow: var(--shadow-sm);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
`;

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const BackToClientButton = styled.button`
  background: transparent;
  border: 2px solid var(--primary-color);
  color: var(--primary-color);
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;

  &:hover {
    background: var(--primary-color);
    color: var(--text-light);
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }
`;

const LogoutButton = styled.button`
  background: var(--primary-color);
  border: none;
  color: var(--text-light);
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;

  &:hover {
    background: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }
`;

const Content = styled.main`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const ProfileCard = styled.div`
  background: var(--surface);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-light);
  margin-bottom: 2rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-light);
`;

const CardTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: flex-end;
  
  /* Mobile styles - full width buttons */
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: stretch;
    gap: 0.75rem;
  }
`;

const EditButton = styled.button`
  background: var(--primary-color);
  color: var(--text-light);
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 600;
  border: none;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;

  &:hover {
    background: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }
  
  /* Mobile styles - full width */
  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 1.5rem;
    white-space: normal;
  }
`;

const DeleteButton = styled.button`
  background: var(--danger);
  color: var(--text-light);
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 600;
  border: none;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;

  &:hover {
    background: var(--danger-dark);
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }
  
  /* Mobile styles - full width */
  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 1.5rem;
    white-space: normal;
  }
`;

const RefreshButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: var(--transition);
  color: var(--text-secondary);

  &:hover {
    background: var(--surface-hover);
    transform: rotate(180deg);
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 0.9rem;
`;

const InfoValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  text-align: right;
  max-width: 60%;
`;

// Enhanced Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-out;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  animation: ${slideIn} 0.4s ease-out;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  color: #333;
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EditIcon = styled.span`
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  line-height: 1;
  transition: all 0.3s ease;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
    background: #f0f0f0;
    transform: scale(1.1);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormSection = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #e9ecef;
`;

const SectionTitle = styled.h3`
  color: #495057;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 2px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  color: #333;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const RequiredStar = styled.span`
  color: #e74c3c;
  font-weight: bold;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const FormInput = styled.input`
  padding: 1rem;
  border: 2px solid ${props => props.$hasError ? '#e74c3c' : props.$disabled ? '#e9ecef' : '#ddd'};
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: ${props => props.$disabled ? '#f8f9fa' : 'white'};
  color: ${props => props.$disabled ? '#6c757d' : '#333'};
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const FormSelect = styled.select`
  padding: 1rem;
  border: 2px solid ${props => props.$hasError ? '#e74c3c' : '#ddd'};
  border-radius: 10px;
  font-size: 1rem;
  background: white;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  position: relative;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:hover {
    border-color: #adb5bd;
    background-color: #f8f9fa;
  }

  &:disabled {
    background-color: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
    opacity: 0.7;
  }

  option {
    padding: 0.5rem;
    font-size: 1rem;
    background: white;
    color: #333;
  }

  option:hover {
    background-color: #667eea;
    color: white;
  }

  /* Custom styling for the dropdown arrow */
  &::-ms-expand {
    display: none;
  }

  /* Firefox */
  &:-moz-focusring {
    color: transparent;
    text-shadow: 0 0 0 #000;
  }

  /* Webkit browsers */
  &::-webkit-select-placeholder {
    color: #adb5bd;
  }

  /* Custom focus styles */
  &:focus-visible {
    outline: 2px solid #667eea;
    outline-offset: 2px;
  }

  /* Animation for selection */
  &:valid {
    border-color: #28a745;
  }

  /* Error state */
  ${props => props.$hasError && `
    border-color: #e74c3c;
    background-color: #fdf2f2;
    
    &:focus {
      border-color: #e74c3c;
      box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
    }
  `}

  /* Success state when value is selected */
  ${props => !props.$hasError && props.value && `
    border-color: #28a745;
    background-color: #f8fff9;
  `}
`;

const FormHelp = styled.small`
  color: #6c757d;
  font-size: 0.8rem;
  font-style: italic;
  margin-top: 0.25rem;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  font-weight: 500;
  animation: ${fadeIn} 0.3s ease-out;
`;

const CancelButton = styled.button`
  background: var(--secondary-color);
  color: var(--text-light);
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 600;
  border: none;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;

  &:hover:not(:disabled) {
    background: var(--secondary-dark);
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  background: var(--primary-color);
  color: var(--text-light);
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 600;
  border: none;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  border-radius: var(--border-radius-lg);
  padding: 3rem;
  margin: 2rem auto;
  max-width: 400px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-light);

  p {
    margin-top: 1rem;
    color: var(--text-primary);
    font-size: 1.1rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-light);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Footer = styled.footer`
  text-align: center;
  padding: 2rem 0;
`;

const FooterText = styled.p`
  color: var(--text-secondary);
  margin: 0;
  font-size: 0.9rem;
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 1rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const LegalLink = styled.a`
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  transition: var(--transition);
  border-bottom: 1px solid var(--border);

  &:hover {
    border-bottom-color: var(--primary-color);
    transform: translateY(-2px);
  }
`;

export default Dashboard; 